/*
<summary>
   This functional component is all about rendering the Tool Check Item Template list/grid.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import toast from 'react-hot-toast';
import Header from '../../shared-components/header/header';
import Grid from '../../shared-components/Grid/Grid';
import { CustomActionFormatter, TooltipTextFormatter } from '../../shared-components/Grid/GridFormatter';
import Pagination from '../../shared-components/Grid/Pagination';
import DeletePopup from '../../shared-components/popup/delete/delete-popup';
import DeletePopupHOC from '../../shared-components/popup/delete/delete-popup-hoc';
import {
  DeleteIcon,
  EditIcon,
  PlusIcon,
  RefreshIcon,
  CloseSidePanelIcon,
} from '../../shared-components/svgs/svgs';
import { formatMessage } from '../../translations/format-message';
import './tool-check-item-template.css';
import { ModuleType } from '../../constants/enums/module-type-enum';
import userSettingEnum from '../../constants/enums/user-setting-enum';
import {
  isOperationPermittedRoleBase,
  showIconsRoleBase,
} from '../../helpers/action-permission-helper';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import AddEditToolCheckItemTemplate from './components/add-edit-tool-check-item-template';
import { useStore } from '../../contexts/store-provider';
import FilterComponent from './components/filter';
import { IToolCheckItemFilter } from '../../models/IToolCheckItemFilter';
import {
  ToolCheckItemDefaultSortColumnOrder,
  ToolCheckItemFilterInitialState,
} from '../../core/initialState/filter-initial-state';
import IDefaultColumSortOrder from '../../models/IDefaultColumSortOrder';
import IPageInfo, { IPageLimitInfo } from '../../models/ICommon';
import FilterPopupHOC from '../../shared-components/hoc/filter-popup-hoc/filter-popup-hoc';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import SlidingPanel from 'react-sliding-side-panel';
import AddToolCheckItemTemplatePopupHOC from './hoc/add-tool-check-item-template-popup-hoc';
import ToolCheckItemTemplateDetails from './tool-check-item-template-details';
import { GridRemoteConstant } from '../../constants/grid-constant';
import noMessageIcon from '../../assets/Images/filterNodata.png';

/**
* This functional component is used to render the Tool Check Item Template list grid along with add, edit, delete and filter actions.
* @param props: Component props containing popup toggle handlers passed by the wrapping HOCs
*/
const ToolCheckItemTemplate = (props: any) => {
  const { preferencesStore, toolCheckItemTemplateStore } = useStore();
  const {
    userSetting,
    setUserSetting,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const {
    checkItemTemplateList,
    GetCheckItemTemplateListService,
    getAllCheckItemTemplateState,
    DeleteCheckItemTemplateService,
    resetDeleteState,
    deleteCheckItemTemplateState,
    getToolCheckItemTemplateList
  } = toolCheckItemTemplateStore;
  const isFirstLoad = useRef(true);

  const [searchTerm, setSearchTerm] = useState<IToolCheckItemFilter>(
    ToolCheckItemFilterInitialState
  );
  const [selectedId, setSelectedId] = useState(-1);
  const [selectedName, setSelectedName] = useState('');
  const [filterObj, setFilterObj] = useState<IToolCheckItemFilter>(
    ToolCheckItemFilterInitialState
  );
  const [columSortOrder, setColumnSortOrder] = useState<IDefaultColumSortOrder>(
    ToolCheckItemDefaultSortColumnOrder
  );

  const [isFilterCleared, setIsFilterCleared] = useState(true);
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [openPanel, setOpenPanel] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const pageLimitOptions: Array<number> = [10, 20, 50, 100];
  const [disablePagination, setDisablePagination] = useState<boolean>(false);

  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  /**
  * This function is used to call the check item template list API with the current filter, sort and paging state.
  * @param CheckItemFilter: Filter criteria to apply to the list request
  * @param filterApplied: Flag indicating a filter was just applied, so the page is reset to 1
  */
  const getCheckItemListServiceHandler = (
    CheckItemFilter?: IToolCheckItemFilter,
    filterApplied?: boolean
  ) => {
    if (!filterApplied) {
      CheckItemFilter = {
        ...filterObj,
        Column: columSortOrder.dataField,
        Order: columSortOrder.order,
      };
    }
    GetCheckItemTemplateListService(
      filterApplied ? 1 : pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      CheckItemFilter
    );
  };

  /**
   * This useEffect is used to close the detail side panel and load the check item template list on initial mount.
   */
  useEffect(() => {
    closePanelhandler();
    getCheckItemListServiceHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This useEffect is used to sync the current page and total pages with the latest check item template list response.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      currentPage: checkItemTemplateList?.PagingDetails?.CurrentPage,
      totalPages: checkItemTemplateList?.PagingDetails?.TotalPages,
      isPagerChange: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkItemTemplateList]);

  /**
   * This useEffect is used to reload the list whenever the current page changes via the pager.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      getCheckItemListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * This useEffect is used to reload the list whenever the page size selection changes.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      getCheckItemListServiceHandler();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);


  /**
   * This useEffect is used to show a success toast, close the delete popup and refresh the list after a successful delete.
   */
  useEffect(() => {
    if (deleteCheckItemTemplateState.success) {
      toast.success(formatMessage('deleted_success'));
      props.deletePopupToggleHandler();
      handleRefresh();
      resetDeleteState();
    }
  }, [deleteCheckItemTemplateState.success]);

  /**
   * This useEffect is used to close the add/edit popup and refresh the list after a successful add or update.
   */
  useEffect(() => {
    if (toolCheckItemTemplateStore.addUpdateCheckItemTemplateState.success) {
      props.addToggleHandler();
      handleRefresh();
      toolCheckItemTemplateStore.resetAddUpdateState();
    }
  }, [toolCheckItemTemplateStore.addUpdateCheckItemTemplateState.success]);

  /**
   * This useEffect is used to close the side panel, reload the list and reset the language
   * changed flag whenever the selected language changes.
   */
  useEffect(() => {
    if (islanguageChanged) {
      closePanelhandler();
      getCheckItemListServiceHandler();
      updateLanguageChangedFlag();
    }
  }, [islanguageChanged]);

  const handleRefresh = () => {
    getCheckItemListServiceHandler();
  };

  const openAddModal = () => {
    setSelectedId(-1);
    closePanelhandler();
    props.addToggleHandler();
  };

  const handleEdit = (row: any, event: any) => {
    setSelectedId(row.Id);
    event.stopPropagation();
    closePanelhandler();
    props.addToggleHandler();
  };

  const handleDeleteClick = (row: any, event: any) => {
    setSelectedId(row.Id);
    setSelectedName(row.TemplateName);
    event.stopPropagation();
    closePanelhandler();
    props.deletePopupToggleHandler();
  };

  const deleteHandler = () => {
    const tenantId = Number(getTenantIdFromLocalStorage()) || 0;
    DeleteCheckItemTemplateService(selectedId, selectedName, tenantId);
  };

  /**
  * This function is used to close the add/edit popup and refresh the list if the add/update was successful.
  */
  const closeModal = () => {
    props.addToggleHandler();
    if (toolCheckItemTemplateStore.addUpdateCheckItemTemplateState.success) {
      handleRefresh();
      toolCheckItemTemplateStore.resetAddUpdateState();
    }
  };

  const actionIcons: any[] = showIconsRoleBase(
    [
      {
        dataField: 'isEdit',
        handler: handleEdit,
        icon: <EditIcon />,
        isDisable: !isOperationPermittedRoleBase(
          ModuleType.ToolCheckItemTemplate,
          'Edit'
        ),
        title: 'edit',
        varient: 'primary',
        action: 'edit',
      },
      {
        dataField: 'isDelete',
        handler: handleDeleteClick,
        icon: <DeleteIcon />,
        isDisable: !isOperationPermittedRoleBase(
          ModuleType.ToolCheckItemTemplate,
          'Delete'
        ),
        title: 'delete',
        varient: 'danger',
        action: 'delete',
      },
    ],
    ModuleType.ToolCheckItemTemplate
  );

  const columns: any = [
    {
      dataField: 'serial_number',
      text: formatMessage('serial_number'),
      classes: 'show-Elipses',
      formatter: (_: any, __: any, rowIndex: number) => rowIndex + 1,
    },
    {
      dataField: 'TemplateName',
      text: formatMessage('template_name'),
      classes: 'show-Elipses',
      formatter: TooltipTextFormatter,
      sort: false,
    },
    {
      dataField: 'Details',
      text: formatMessage('remarks'),
      classes: 'show-Elipses',
      formatter: TooltipTextFormatter,
      sort: false,
    },
    {
      dataField: '',
      formatter: CustomActionFormatter(actionIcons),
      text: formatMessage('actions'),
      classes: 'last-column',
      hidden: actionIcons.length > 0 ? false : true,
    },
  ];

  /**
  * This function is used to select a grid row and open/close the detail side panel accordingly.
  * @param e: Click event
  * @param row: Row data that was clicked
  * @param rowIndex: Index of the clicked row
  */
  const rowEvents = (e: any, row: any, rowIndex: number) => {
    setSelectedRow(row);
    if (selectedRow.Id === undefined || selectedRow.Id !== row.Id) {
      setOpenPanel(true);
    } else {
      setOpenPanel(!openPanel);
    }
  };

  function rowClassFormat(row: any, rowId: number) {
    let className: string = '';
    if (selectedRow?.Id && row.Id === selectedRow.Id) {
      className = 'bg-row SelectedRow';
    }
    return className;
  }

  const onPageLimitChanged = (evt: any) => {
    setPageInfo({ ...pageInfo, currentPage: 1, isPagerChange: false });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  /**
  * This function is used to apply the filter values, reload the list and close the filter popup.
  * @param object: Filter values submitted from the filter form
  */
  const filterHandler = (object: IToolCheckItemFilter) => {
    const filters = {
      ...object,
      Column: columSortOrder.dataField,
      Order: columSortOrder.order,
    };
    getCheckItemListServiceHandler(filters, true);
    setFilterObj({ ...filters });
    props.filterPopupToggleHandler();
    closePanelhandler();
    setIsFilterCleared(false);
  };

  /**
  * This function is used to update the filter values, and reload the list when the filter is cleared.
  * @param obj: Filter values
  * @param isfilterCleard: Flag indicating the filter was cleared
  */
  const filterParams = (obj: IToolCheckItemFilter, isfilterCleard: boolean) => {
    if (isfilterCleard) {
      setFilterObj({ ...obj });
      getCheckItemListServiceHandler(obj, true);
      closePanelhandler();
      setIsFilterCleared(true);
    } else {
      setFilterObj({ ...obj });
    }
    props.filterPopupToggleHandler();
  };

  const closePanelhandler = () => {
    setSelectedRow({});
    setOpenPanel(false);
  };

  /**
  * This function is used to update the sort order and reload the list when the grid is sorted, ignoring the initial grid load call.
  * @param type: Grid table change type
  * @param newState: New grid state including sort field and order
  */
  const handleTableChange = (type: any, newState: any) => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    setColumnSortOrder((prevState) => ({
      ...prevState,
      dataField: newState.sortField,
      order: newState.sortOrder,
    }));

    const filter = {
      ...filterObj,
      Column: newState.sortField,
      Order: newState.sortOrder,
    };
    getCheckItemListServiceHandler(filter);
  };

  return (
    <>
      {props.showAddCheckItemTemplatePopup && (
        <AddEditToolCheckItemTemplate
          id={selectedId}
          modalClosed={closeModal}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_tool_check_item_template"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_tool_check_item_template_confirm"
          isLoading={deleteCheckItemTemplateState.inProgress}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <Header
            className="col"
            label="tool_check_item_template"
          />
          <div className="col-lg-7 col-md-6 col-sm-6">
            <ul className="add-list">
              {/* <li>
                <FilterComponent
                  initialValues={filterObj}
                  id={0}
                  submitHandler={filterHandler}
                  filterParmas={filterParams}
                  onModalClosed={props.filterPopupToggleHandler}
                  isFilterCleared={isFilterCleared}
                  isLoading={false}
                  isDisable={false}
                  isFilterApplied={isFilterApplied}
                  setIsFilterApplied={setIsFilterApplied}
                />
              </li> */}
              <li title={formatMessage('refresh')}>
                <a
                  className="btn btn-light"
                  onClick={handleRefresh}
                >
                  <RefreshIcon />
                </a>
              </li>
              {isOperationPermittedRoleBase(
                ModuleType.ToolCheckItemTemplate,
                'Add'
              ) && (
                <li title={formatMessage('add_tool_check_item')}>
                  <a
                    className="btn btn-secondary"
                    onClick={openAddModal}
                  >
                    <span className="me-1">
                      <PlusIcon />
                    </span>
                    <FormattedMessage id="add_tool_check_item" />
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      <div className={`${openPanel ? 'panelOpen' : ''}`}>
        <div className="card card-page">
          {(getAllCheckItemTemplateState.inProgress ||
            deleteCheckItemTemplateState.inProgress ||
            toolCheckItemTemplateStore.addUpdateCheckItemTemplateState
              .inProgress) && (
            <FormLoader
              loading={
                getAllCheckItemTemplateState.inProgress ||
                deleteCheckItemTemplateState.inProgress ||
                toolCheckItemTemplateStore.addUpdateCheckItemTemplateState
                  .inProgress
              }
            />
          )}
          {getToolCheckItemTemplateList &&
          getToolCheckItemTemplateList.length > 0 ? (
            <>
              <div className="tableDesign row-eq-height tableDevices p-3 changingPanel">
                <div className="table-responsive">
                  <Grid
                    data={getToolCheckItemTemplateList}
                    columns={columns}
                    keyField="Id"
                    loading={getAllCheckItemTemplateState.inProgress}
                    rowClasses={rowClassFormat}
                    rowEvents={{ onClick: rowEvents }}
                    hover={true}
                    sort={{ ...columSortOrder }}
                    remote={{ ...GridRemoteConstant }}
                    onTableChange={handleTableChange}
                  />
                </div>
              </div>
              <div className="row no-gutters p-3 pt-0 tableFooter">
                <div className="col-lg-3 col-md-6 col-sm-6 pt-1">
                  <FormattedMessage id="show" />
                  <select
                    className="pageLimit"
                    onChange={onPageLimitChanged}
                    value={pageLimitInfo.pageLimit}
                  >
                    {pageLimitOptions.map((op: any) => (
                      <option
                        key={op}
                        value={op}
                      >
                        {op}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-9 col-md-6 col-sm-6 d-flex justify-content-sm-end">
                  <Pagination
                    isRefreshPage={false}
                    totalPages={pageInfo.totalPages}
                    pageLimit={pageLimitInfo.pageLimit}
                    onPageChanged={onPageChanged}
                    disablePagination={disablePagination}
                    setIsRefreshPage={setIsRefreshPage}
                    currentPage={pageInfo.currentPage}
                    totalRecords={
                      checkItemTemplateList?.PagingDetails?.TotalRecords || 0
                    }
                  />
                </div>
              </div>
            </>
          ) : (
            !getAllCheckItemTemplateState.inProgress && (
              <div className="message">
                <div className="d-flex message-area">
                  <div className="no-data-img">
                    <img
                      src={noMessageIcon}
                      alt=""
                      className="no_message"
                    />
                  </div>
                  <div>
                    <h3 className="fw-bold display-6">
                      <FormattedMessage id="no_record" />
                    </h3>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
      <SlidingPanel
        type={'right'}
        isOpen={openPanel}
        noBackdrop={false}
        size={100}
      >
        <div className="bg_slide panel">
          <div className="row bb p-2 pl-4 g-0 align-items-center ">
            <div className="col-lg-10 col-md-10 col-sm-10 col-xs-10">
              <h2 className="fw-bold mb-0 mt-1 ms-2 py-2">
                {selectedRow.CheckItemName}
              </h2>
            </div>
            <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 close-icon-detail">
              <span title={formatMessage('close')}>
                <CloseSidePanelIcon closePanelHandler={closePanelhandler} />
              </span>
            </div>
          </div>
          <div className="group_detailsWrapper pb-3">
            <ToolCheckItemTemplateDetails selectedRowId={selectedRow.Id} />
          </div>
        </div>
      </SlidingPanel>
    </>
  );
};

export default FilterPopupHOC(
  AddToolCheckItemTemplatePopupHOC(DeletePopupHOC(observer(ToolCheckItemTemplate)))
);
